import {
    IAccountResponse,
    ICouponResponse,
    IPicTimeCouponResponse,
    IReferralCodeResponse,
    TGetNextPaymentResponse
} from '~/types/server.types';
import {
    IBaseCancellationData,
    IBillingInfoRequest,
    IInvoice,
    IPauseSubscriptionData,
    IRecurlyPurchasePreviewResponse,
    IRecurlySubscriptionChangeResponse,
    IRecurlyTokenRequest,
    IRecurlyUpgradeRequest,
    TPlanCode,
    INextPaymentAfterPriceChangeResponse
} from '../../types/membership.types';
import { TFeature } from '~/components/FeatureRestriction/types/feature-restriction.types';
import { BaseHttpAdapter } from '~/services/adapters/base-http.adapter';

export class MembershipAdapter extends BaseHttpAdapter {
    async getCoupon(couponName: string, account_id?: string) {
        return this.apiService.apiRequest<ICouponResponse>({
            method: 'get',
            url: `/api/v2/account/${account_id}/coupons/${couponName}`
        });
    }

    getReferralCode(code: string) {
        return this.apiService.apiRequest<IReferralCodeResponse>({
            method: 'get',
            url: `/api/v2/referrals/referral_code/${code}`
        });
    }

    async saveReferral(data) {
        return this.apiService.apiRequest({
            method: 'put',
            url: '/api/v2/referrals/save_referral',
            data
        });
    }

    async purchaseSubscription(accountId: string, data) {
        return this.apiService.apiRequest<{
            account: IAccountResponse;
        }>({
            method: 'post',
            url: `/api/v2/account/${accountId}/subscription`,
            data
        });
    }

    canChangePlan(accountId: string, selectedTierCode: TPlanCode) {
        return this.apiService.apiRequest<{
            can_change: boolean;
            blocking_features: TFeature[];
        }>({
            method: 'get',
            url: `/api/v2/account/${accountId}/can_change_to_pricing_plan`,
            params: {
                plan_code: selectedTierCode
            }
        });
    }

    async _previewPurchase(accountId: string, data: IRecurlyTokenRequest) {
        return this.apiService.apiRequest<IRecurlyPurchasePreviewResponse>({
            method: 'post',
            url: `/api/v2/account/${accountId}/subscription/preview`,
            data
        });
    }

    async _getPlansPriceUpdate(accountId: string) {
        return this.apiService.apiRequest<INextPaymentAfterPriceChangeResponse>(
            {
                method: 'get',
                url: `/api/v2/account/${accountId}/plans_data_for_prices_update_2025`
            }
        );
    }

    async getNextPayment(accountId: string) {
        return this.apiService.apiRequest<TGetNextPaymentResponse>({
            method: 'get',
            url: `/api/v2/account/${accountId}/preview_renewal`
        });
    }

    async reactivateSubscription(accountId: string) {
        return this.apiService.apiRequest({
            method: 'put',
            url: `/api/v2/account/${accountId}/reactivate_subscription`
        });
    }

    async removeSubscriptionChange(accountId: string) {
        return this.apiService.apiRequest({
            method: 'delete',
            url: `/api/v2/account/${accountId}/remove_subscription_change`
        });
    }

    async _previewUpgrade(accountId: string, data: IRecurlyUpgradeRequest) {
        return this.apiService.apiRequest<IRecurlyPurchasePreviewResponse>({
            method: 'post',
            url: `/api/v2/account/${accountId}/subscription/change/preview`,
            data
        });
    }

    async _changeSubscription(accountId: string, data: IRecurlyUpgradeRequest) {
        return this.apiService.apiRequest<IRecurlySubscriptionChangeResponse>({
            method: 'post',
            url: `/api/v2/account/${accountId}/subscription/change`,
            data
        });
    }

    async _getBillingInfo(accountId: string) {
        return this.apiService.apiRequest<any>({
            method: 'get',
            url: `/api/v2/account/${accountId}/subscription/billing_info`
        });
    }

    async _updateBillingInfo(accountId: string, data: IBillingInfoRequest) {
        return this.apiService.apiRequest<{
            account: IAccountResponse;
        }>({
            method: 'put',
            url: `/api/v2/account/${accountId}/subscription/billing_info`,
            data
        });
    }

    async _updateNextPlanPeriod(accountId: string, data) {
        return this.apiService.apiRequest({
            method: 'post',
            url: `/api/v2/account/${accountId}/next_period`,
            data
        });
    }

    async resumePausedSubscription(accountId: string) {
        return this.apiService.apiRequest({
            method: 'put',
            url: `/api/v2/account/${accountId}/subscription/resume`
        });
    }

    async _cancelPauseSubscription(accountId: string, data) {
        return this.apiService.apiRequest({
            method: 'put',
            url: `/api/v2/account/${accountId}/subscription/pause`,
            data
        });
    }

    async _getInvoiceHistory(
        accountId: string,
        begin_time: Date,
        end_time: Date
    ) {
        return this.apiService.apiRequest<IInvoice[]>({
            method: 'get',
            url: `/api/v2/account/${accountId}/invoices`,
            params: {
                begin_time: begin_time.toISOString(),
                end_time: end_time.toISOString()
            }
        });
    }

    async _getBlockedCoupons() {
        return this.apiService.apiRequest<Record<string, string>[]>({
            method: 'get',
            url: `/api/v2/coupon/hidden_recurly_coupon`,
            params: {}
        });
    }

    async isEligibleForCoupon(accountId: string) {
        return this.apiService.apiRequest<boolean>({
            method: 'get',
            url: `/api/v2/coupon/${accountId}/is_eligible_for_coupon`,
            params: {}
        });
    }

    async _checkPicTimeCouponEligibility(accountId: string) {
        return this.apiService.apiRequest<IPicTimeCouponResponse>({
            method: 'get',
            url: `/api/v2/account/${accountId}/pictime/coupon_eligibility`
        });
    }

    cancelPricingPlan(
        accountId: string,
        { reason, feedback, other_reason }: IBaseCancellationData
    ) {
        return this.apiService.apiRequest<IAccountResponse>({
            method: 'put',
            url: `/api/v2/account/${accountId}/subscription/cancel`,
            data: {
                cancellation_reasons: [reason],
                other_reason,
                ...(feedback && { extra_info: feedback })
            }
        });
    }

    pausePricingPlan(
        accountId: string,
        { reason, pauseDuration, feedback }: IPauseSubscriptionData
    ) {
        return this.apiService.apiRequest<IAccountResponse>({
            method: 'put',
            url: `/api/v2/account/${accountId}/subscription/pause`,
            data: {
                cancellation_reasons: [reason],
                remaining_cycles: pauseDuration,
                ...(feedback && { extra_info: feedback })
            }
        });
    }

    terminateSubscription(
        accountId: string,
        { reason, feedback }: IBaseCancellationData
    ) {
        return this.apiService.apiRequest<null>({
            method: 'delete',
            url: `/api/v2/account/${accountId}/subscription/terminate`,
            params: {
                cancellation_reasons: [reason],
                ...(feedback && { extra_info: feedback })
            }
        });
    }
}
